'use client';

import { useEffect, useState, useCallback } from 'react';
import {
  MapPin,
  ShoppingBag,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Navigation,
  Loader2,
} from 'lucide-react';
import { format, isValid } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { getAvatarColor, getAvatarInitials } from '@/components/contact-avatar';
import { OrderDetailPanel } from '@/components/orders/order-detail-panel';
import { ConfirmOrderDialog } from '@/components/orders/confirm-order-dialog';
import { customersClient } from '@/lib/customers-client';
import type { Customer, CustomerAddress, PaginationMeta } from '@/lib/customers-client';
import { ordersClient, type Order, type OrderStatus } from '@/lib/orders-client';
import { STATUS_COLORS } from '@/lib/orders-client';

const ORDERS_PER_PAGE = 5;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phoneNumber: string;
  contactName?: string;
  /** Navigate to the order's WhatsApp conversation (closes the dialog first). */
  onViewConversation?: (conversationId: string) => void;
};

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    if (!isValid(d)) return '';
    return format(d, 'dd/MM/yyyy HH:mm');
  } catch {
    return '';
  }
}

function AddressCard({ address }: { address: CustomerAddress }) {
  const { t } = useTranslation('conversations');
  const [expanded, setExpanded] = useState(false);

  const addressSourceLabel = (source: string): string =>
    t(`profile.addressSource.${source}`, { defaultValue: source });

  const details: Array<{ label: string; value: string }> = [];
  if (address.street) details.push({ label: t('profile.addressDetails.street'), value: address.street });
  if (address.doorNumber) details.push({ label: t('profile.addressDetails.doorNumber'), value: address.doorNumber });
  if (address.apartment) details.push({ label: t('profile.addressDetails.apartment'), value: address.apartment });
  if (address.neighborhood) details.push({ label: t('profile.addressDetails.neighborhood'), value: address.neighborhood });
  if (address.city) details.push({ label: t('profile.addressDetails.city'), value: address.city });
  if (address.state) details.push({ label: t('profile.addressDetails.state'), value: address.state });
  if (address.zipCode) details.push({ label: t('profile.addressDetails.zipCode'), value: address.zipCode });
  if (address.country) details.push({ label: t('profile.addressDetails.country'), value: address.country });
  if (address.resolvedAddress && address.resolvedAddress !== address.raw) {
    details.push({ label: t('profile.addressDetails.resolvedAddress'), value: address.resolvedAddress });
  }
  if (address.customerInput && address.customerInput !== address.raw) {
    details.push({ label: t('profile.addressDetails.customerInput'), value: address.customerInput });
  }
  if (address.source) {
    details.push({ label: t('profile.addressDetails.source'), value: addressSourceLabel(address.source) });
  }
  if (address.geocodeQuality) details.push({ label: t('profile.addressDetails.geocodeQuality'), value: address.geocodeQuality });

  const hasCoordinates = address.coordinates?.lat != null && address.coordinates?.lng != null;
  const mapsUrl = hasCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${address.coordinates!.lat}%2C${address.coordinates!.lng}`
    : null;

  const hasDetails = details.length > 0 || hasCoordinates;

  return (
    <div className="rounded-lg border p-3">
      <div className="flex items-start gap-3">
        <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <p className="min-w-0 flex-1 text-sm font-medium break-words">{address.raw}</p>
            {address.label && (
              <Badge variant="secondary" className="flex-shrink-0 text-[10px]">
                {address.label}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {t('profile.lastUsed', { date: formatDate(address.lastUsedAt) })}
          </p>
        </div>
      </div>

      {hasDetails && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-2 ml-7 flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          aria-expanded={expanded}
        >
          <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          {expanded ? t('profile.hideDetails') : t('profile.showDetails')}
        </button>
      )}

      {expanded && (
        <div className="mt-2 ml-7 space-y-1 border-l pl-3">
          {details.map((detail) => (
            <div key={detail.label} className="flex justify-between gap-3 text-xs">
              <span className="flex-shrink-0 text-muted-foreground">{detail.label}</span>
              <span className="min-w-0 break-words text-right">{detail.value}</span>
            </div>
          ))}
          {hasCoordinates && (
            <div className="flex justify-between gap-3 text-xs">
              <span className="flex-shrink-0 text-muted-foreground">{t('profile.addressDetails.coordinates')}</span>
              <a
                href={mapsUrl!}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
              >
                <Navigation className="h-3 w-3" />
                {address.coordinates!.lat.toFixed(5)}, {address.coordinates!.lng.toFixed(5)}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, onSelect }: { order: Order; onSelect: () => void }) {
  const { t } = useTranslation('conversations');
  const statusLabel = t(`profile.orderStatus.${order.status}`, { defaultValue: order.status });
  const statusColor = STATUS_COLORS[order.status] || 'bg-muted text-muted-foreground';

  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50"
      title={t('profile.viewOrderDetail')}
    >
      <ShoppingBag className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{formatDate(order.createdAt)}</p>
          <Badge className={statusColor}>{statusLabel}</Badge>
        </div>
        <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            {t('profile.itemsCount', {
              count: order.items.reduce(
                (sum: number, item: { quantity: number }) => sum + item.quantity,
                0
              ),
            })}
          </span>
          <span>
            {order.currency} {order.total.toFixed(2)}
          </span>
          {order.deliveryType && (
            <span>
              {order.deliveryType === 'delivery'
                ? t('profile.deliveryType.delivery')
                : t('profile.deliveryType.pickup')}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground/60" />
    </button>
  );
}

export function CustomerProfileDialog({ open, onOpenChange, phoneNumber, contactName, onViewConversation }: Props) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('conversations');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [confirmingOrder, setConfirmingOrder] = useState<Order | null>(null);

  const fetchProfile = useCallback(
    async (page: number) => {
      const isInitial = page === 1 && !customer;
      if (isInitial) setLoading(true);
      else setOrdersLoading(true);
      setError(null);
      setNotFound(false);

      try {
        const res = await customersClient.getProfile(phoneNumber, {
          page,
          limit: ORDERS_PER_PAGE,
        });
        setCustomer(res.data.customer);
        setOrders(res.data.orders);
        setPagination(res.data.pagination);
      } catch (err) {
        const is404 = err instanceof Error && err.message.includes('(404)');
        if (is404) {
          setNotFound(true);
        } else {
          console.error('Failed to load customer profile', err);
          setError(t('profile.loadError'));
        }
      } finally {
        setLoading(false);
        setOrdersLoading(false);
      }
    },
    [phoneNumber, customer, t],
  );

  useEffect(() => {
    if (!open || !phoneNumber) return;
    setCustomer(null);
    setOrders([]);
    setPagination(null);
    setNotFound(false);
    setError(null);
    setSelectedOrder(null);
    setConfirmingOrder(null);
    fetchProfile(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phoneNumber]);

  const handlePageChange = (newPage: number) => {
    fetchProfile(newPage);
  };

  // Keep the profile's order list, the open detail, and the orders page (react-query
  // cache) in sync after a mutation from inside the dialog.
  const patchOrder = (orderId: string, patch: Partial<Order>) => {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...patch } : o)));
    setSelectedOrder((prev) => (prev?.id === orderId ? { ...prev, ...patch } : prev));
    void queryClient.invalidateQueries({ queryKey: ['orders'] });
  };

  // Mirrors the orders page: pending → confirmed goes through the confirmation dialog
  // (which offers to notify the customer on WhatsApp).
  const handleStatusChange = async (orderId: string, newStatus: OrderStatus) => {
    if (newStatus === 'confirmed') {
      const order =
        orders.find((o) => o.id === orderId) ??
        (selectedOrder?.id === orderId ? selectedOrder : null);
      if (order && order.status === 'pending') {
        setConfirmingOrder(order);
        return;
      }
    }

    try {
      await ordersClient.updateStatus(orderId, newStatus);
      patchOrder(orderId, { status: newStatus });
    } catch (err) {
      console.error('Failed to update order status:', err);
    }
  };

  const handleRetryPosPush = async (orderId: string) => {
    try {
      const response = await ordersClient.retryPosPush(orderId);
      patchOrder(orderId, response.data);
    } catch (err) {
      console.error('Failed to retry POS push:', err);
    }
  };

  const handleConfirmOrderComplete = (orderId: string) => {
    patchOrder(orderId, { status: 'confirmed' });
    setConfirmingOrder(null);
  };

  const handleViewConversation = onViewConversation
    ? (conversationId: string) => {
        onOpenChange(false);
        onViewConversation(conversationId);
      }
    : undefined;

  const displayName = customer?.name || contactName || phoneNumber;
  const initials = getAvatarInitials(customer?.name || contactName, phoneNumber);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Bounded height + flex column so the tab bodies below get a definite height
          to scroll inside (a bare max-h on ScrollArea doesn't clip — it overflows). */}
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-md">
        <DialogHeader className="shrink-0">
          <DialogTitle className="sr-only">{t('profile.title')}</DialogTitle>
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback
                className="text-white font-medium"
                style={{ backgroundColor: getAvatarColor(customer?.name || contactName, phoneNumber) }}
              >
                {loading ? <User className="h-5 w-5" /> : initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              {loading ? (
                <>
                  <Skeleton className="mb-1 h-5 w-32" />
                  <Skeleton className="h-3 w-24" />
                </>
              ) : (
                <>
                  <p className="truncate text-sm font-medium">{displayName}</p>
                  {customer?.name && customer.phone !== displayName && (
                    <p className="text-xs text-muted-foreground">{customer.phone}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </DialogHeader>

        {notFound ? (
          <div className="py-10 text-center">
            <ShoppingBag className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-muted-foreground">{t('profile.noOrdersYet')}</p>
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-950/40 p-4 text-center text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        ) : loading ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : selectedOrder ? (
          // Order detail — the exact same panel the orders page shows in its sidebar.
          <div className="flex min-h-0 flex-1 flex-col">
            <Button
              variant="ghost"
              size="sm"
              className="mb-1 self-start"
              onClick={() => setSelectedOrder(null)}
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Volver al perfil
            </Button>
            <div className="flex min-h-0 flex-1 overflow-hidden rounded-md border">
              <OrderDetailPanel
                embedded
                order={selectedOrder}
                onClose={() => setSelectedOrder(null)}
                onStatusChange={handleStatusChange}
                onViewConversation={handleViewConversation}
                onRetryPosPush={handleRetryPosPush}
              />
            </div>
          </div>
        ) : (
          <Tabs defaultValue="addresses" className="mt-2 flex min-h-0 flex-1 flex-col">
            <TabsList className="w-full shrink-0">
              <TabsTrigger value="addresses" className="flex-1">
                {t('profile.addresses')}
              </TabsTrigger>
              <TabsTrigger value="orders" className="flex-1">
                {t('profile.orders')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="addresses" className="min-h-0 flex-1 overflow-y-auto pr-1">
              {customer?.addresses?.length ? (
                <div className="space-y-2">
                  {customer.addresses.map((addr) => (
                    <AddressCard key={addr.id} address={addr} />
                  ))}
                </div>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {t('profile.noAddresses')}
                </p>
              )}
            </TabsContent>

            <TabsContent value="orders" className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                {ordersLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-[60px] w-full" />
                    ))}
                  </div>
                ) : orders.length ? (
                  <div className="space-y-2">
                    {orders.map((order) => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        onSelect={() => setSelectedOrder(order)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    {t('profile.noPreviousOrders')}
                  </p>
                )}
              </div>

              {pagination && pagination.totalPages > 1 && (
                <div className="mt-3 flex shrink-0 items-center justify-between border-t pt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page <= 1 || ordersLoading}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    {t('profile.previous')}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {ordersLoading ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      `${pagination.page} / ${pagination.totalPages}`
                    )}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!pagination.hasMore || ordersLoading}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    {t('profile.next')}
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        <ConfirmOrderDialog
          order={confirmingOrder}
          onConfirm={handleConfirmOrderComplete}
          onCancel={() => setConfirmingOrder(null)}
        />
      </DialogContent>
    </Dialog>
  );
}
